import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, Eye } from 'lucide-react';
import { DataTable, Column } from '../../components/DataTable';
import { roleApi } from '../../services/api';
import { Role } from '../../types';
import RoleForm from './components/RoleForm';
import RoleDetailsPanel from './components/RoleDetailsPanel';
import { showSuccess, showError } from '../../utils/toast';

const RolesManagementPage: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 15,
    total: 0,
    lastPage: 1,
  });
  const [filters, setFilters] = useState({
    search: '',
    sort_by: 'created_at',
    sort_order: 'desc' as 'asc' | 'desc',
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [viewingRole, setViewingRole] = useState<Role | null>(null);

  const loadRoles = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        page: pagination.currentPage,
        per_page: pagination.perPage,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order,
      };
      if (filters.search) params.search = filters.search;

      const response = await roleApi.list(params);
      setRoles(response.data);
      setPagination((prev) => ({
        ...prev,
        currentPage: response.current_page,
        perPage: response.per_page,
        total: response.total,
        lastPage: response.last_page,
      }));
    } catch (error) {
      console.error('Failed to load roles:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.perPage, filters.search, filters.sort_by, filters.sort_order]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handlePerPageChange = (perPage: number) => {
    setPagination((prev) => ({ ...prev, perPage, currentPage: 1 }));
  };

  const handleSort = (sortBy: string, sortOrder: 'asc' | 'desc') => {
    setFilters((prev) => ({ ...prev, sort_by: sortBy, sort_order: sortOrder }));
  };

  const handleSearch = (search: string) => {
    setFilters((prev) => ({ ...prev, search }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleDelete = async (roleId: number) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;
    try {
      await roleApi.delete(roleId);
      showSuccess('Role deleted successfully');
      loadRoles();
    } catch (error: any) {
      console.error('Failed to delete role:', error);
      const errorMsg = error?.response?.data?.message || error?.message || 'Failed to delete role';
      showError(errorMsg);
    }
  };

  const columns: Column<Role>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      render: (role) => <div className="font-medium theme-text-primary">{role.name}</div>,
    },
    {
      key: 'permissions_count',
      header: 'Permissions',
      sortable: false,
      render: (role) => (
        <span className="text-sm text-gray-600">
          {role.permissions_count || 0} permissions
        </span>
      ),
    },
    {
      key: 'users_count',
      header: 'Users',
      sortable: false,
      render: (role) => (
        <span className="text-sm text-gray-600">
          {role.users_count || 0} users
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      sortable: true,
      render: (role) => (
        <span className="text-sm text-gray-500">
          {role.created_at ? new Date(role.created_at).toLocaleDateString() : '-'}
        </span>
      ),
    },
  ];

  return (
    <div className="glass rounded-2xl p-3 md:p-4 relative">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl font-semibold theme-card-title">Roles & Permissions</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-1.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-3 py-1.5 rounded-lg hover:shadow-lg transition-shadow text-sm"
        >
          <Plus className="w-3.5 h-3.5" />
          Create Role
        </button>
      </div>

      <DataTable
        columns={columns}
        data={roles}
        loading={loading}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPerPageChange={handlePerPageChange}
        onSort={handleSort}
        sortBy={filters.sort_by}
        sortOrder={filters.sort_order}
        searchable
        onSearch={handleSearch}
        searchPlaceholder="Search roles..."
        emptyMessage="No roles found"
        actions={(role) => (
          <div className="flex items-center gap-1.5 justify-end">
            <button
              onClick={() => setViewingRole(role)}
              className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
              title="View Details"
            >
              <Eye className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setEditingRole(role)}
              className="p-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
              title="Edit"
            >
              <Edit className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => handleDelete(role.id)}
              className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
              title="Delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      />

      {showCreateModal && (
        <RoleForm
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            loadRoles();
          }}
        />
      )}

      {editingRole && (
        <RoleForm
          role={editingRole}
          onClose={() => setEditingRole(null)}
          onSuccess={() => {
            setEditingRole(null);
            loadRoles();
          }}
        />
      )}

      {viewingRole && (
        <RoleDetailsPanel
          roleId={viewingRole.id}
          onClose={() => setViewingRole(null)}
          onUpdate={loadRoles}
        />
      )}
    </div>
  );
};

export default RolesManagementPage;

